/**
 * components/feedback/ErrorBoundary.jsx — Catches render crashes so one broken
 * page shows a friendly reload screen instead of blanking the whole app.
 *
 * Also recovers from stale lazy-loaded chunks after a deploy
 * ("Loading chunk N failed"): the reload button fetches the new build.
 */
import React from 'react';

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error('[ErrorBoundary]', error, info?.componentStack);
    }

    componentDidUpdate(prevProps) {
        // Navigating to another page clears the error so the app is usable again.
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null });
        }
    }

    render() {
        if (!this.state.error) return this.props.children;

        const isChunk = /Loading (CSS )?chunk|ChunkLoadError/i.test(String(this.state.error?.message || this.state.error?.name));
        return (
            <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'inherit' }}>
                <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 14, boxShadow: '0 10px 30px rgba(34,50,78,0.12)', overflow: 'hidden', textAlign: 'center' }}>
                    <div style={{ height: 4, background: '#C9721C' }} />
                    <div style={{ padding: '28px 24px' }}>
                        <h2 style={{ margin: '0 0 8px', color: '#22324E', fontSize: 22 }}>Something went wrong</h2>
                        <p style={{ margin: '0 0 20px', color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
                            {isChunk
                                ? 'A new version of RoofingAssist was released. Reload to get the latest.'
                                : 'This page hit an unexpected error. Your saved work is safe — reloading usually fixes it.'}
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => window.location.reload()}
                                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#22324E', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                            >
                                Reload page
                            </button>
                            <button
                                onClick={() => { window.location.href = '/dashboard'; }}
                                style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#22324E', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                            >
                                Go to dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
